import React from "react";
import { useParams, Link } from "react-router-dom";
import { FiMapPin, FiBriefcase, FiUsers, FiSend } from "react-icons/fi";
import { openJobs } from "../data/jobs";

const CareerJobDetail = () => {
  const { slug } = useParams();
  const job = openJobs.find((j) => j.slug === slug);

  if (!job) {
    return (
      <section className="bg-paper py-24 text-center">
        <h1 className="text-2xl font-bold text-brand">Role not found</h1>
        <p className="mt-3 text-muted">
          This opening may have closed. See what's currently available.
        </p>
        <Link to="/careers" className="mt-6 inline-block text-action hover:underline">
          ← Back to careers
        </Link>
      </section>
    );
  }

  const mailtoHref = `mailto:info@nirkana.com?subject=${encodeURIComponent(
    `Application: ${job.title}`
  )}&body=${encodeURIComponent("Hi Nirkana,\n\nI'd like to apply for this role. My relevant experience:\n\n")}`;

  return (
    <div>
      <section className="bg-gradient-to-r from-brand to-action px-6 py-14 lg:px-10 lg:py-20">
        <div className="max-w-content mx-auto">
          <Link to="/careers" className="text-sm text-paper/70 hover:text-paper">
            ← All openings
          </Link>
          <h1 className="mt-3 text-3xl font-bold text-paper sm:text-4xl">{job.title}</h1>
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm text-paper/80">
            <span className="flex items-center gap-1.5"><FiMapPin size={14} /> {job.location}</span>
            <span className="flex items-center gap-1.5"><FiBriefcase size={14} /> {job.experience}</span>
            <span className="flex items-center gap-1.5"><FiUsers size={14} /> {job.openings} opening{job.openings > 1 ? "s" : ""}</span>
          </div>

          <div className="mt-8">
            <a
              href={mailtoHref}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-paper px-6 py-3 text-base font-medium text-brand hover:bg-paper/90 transition-colors"
            >
              <FiSend size={16} />
              Send your profile to info@nirkana.com
            </a>
          </div>
        </div>
      </section>

      <section className="bg-paper py-14 lg:py-20">
        <div className="max-w-content mx-auto grid grid-cols-1 gap-12 px-6 lg:grid-cols-[1fr_320px] lg:gap-16 lg:px-10">
          <div>
            <div>
              <h2 className="text-lg font-semibold text-charcoal">Role & Responsibilities</h2>
              <ul className="mt-3 space-y-2.5 text-sm text-muted">
                {job.responsibilities.map((r) => (
                  <li key={r} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-action" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <h2 className="text-lg font-semibold text-charcoal">Preferred candidate profile</h2>
              <ul className="mt-3 space-y-2.5 text-sm text-muted">
                {job.profile.map((p) => (
                  <li key={p} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-action" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <h2 className="text-lg font-semibold text-charcoal">Perks and benefits</h2>
              <ul className="mt-3 space-y-2.5 text-sm text-muted">
                {job.perks.map((p) => (
                  <li key={p} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-action" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="h-fit rounded-2xl border border-line bg-cloud p-6">
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-muted">Employment type</dt>
                <dd className="mt-0.5 font-medium text-charcoal">{job.employmentType}</dd>
              </div>
              <div>
                <dt className="text-muted">Experience</dt>
                <dd className="mt-0.5 font-medium text-charcoal">{job.experience}</dd>
              </div>
              <div>
                <dt className="text-muted">Compensation</dt>
                <dd className="mt-0.5 font-medium text-charcoal">{job.compensation}</dd>
              </div>
              <div>
                <dt className="text-muted">Department</dt>
                <dd className="mt-0.5 font-medium text-charcoal">{job.department}</dd>
              </div>
              <div>
                <dt className="text-muted">Work model</dt>
                <dd className="mt-0.5 font-medium text-charcoal">{job.workModel}</dd>
              </div>
              {job.pfStatus && (
                <div>
                  <dt className="text-muted">Provident Fund (PF)</dt>
                  <dd className="mt-0.5 text-charcoal">{job.pfStatus}</dd>
                </div>
              )}
            </dl>

            <div className="mt-6 border-t border-line pt-5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">Key skills</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {job.skills.map((s) => (
                  <span key={s} className="rounded-full bg-paper px-3 py-1 text-xs text-charcoal">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default CareerJobDetail;
